import { useEffect, useMemo, useCallback } from 'react';
// material-ui
import { Autocomplete, Box, FormControl, IconButton, TextField, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { dispatch } from 'store';
import { getFeatures } from '_api/features';
import { setUserDomain } from 'store/reducers/auth';
import StarOutlined from '@ant-design/icons/StarOutlined';
import StarFilled from '@ant-design/icons/StarFilled';
import { UserLayer } from 'pages/admin/constant';
import { resetFilters } from 'store/reducers/filters';
import { authSelector } from 'selectors/auth.js';
import { resetActiveTableView } from 'store/reducers/tableViews';
import { setDefaultDomain } from '_api/account';
import { useTheme } from '@mui/material/styles';
import { showToast } from 'utils/notifications';
import { getPersonalView } from '_api/tableViews';

const DomainSelection = () => {
  const sharedDomainUuid = localStorage.getItem('sharedDomainUuid');
  const dmID = localStorage.getItem('domainID');
  const {
    domainID,
    user: { domains: domainsList, uuid: userID },
    userProfile: { default_domain_uuid: defaultDomainUuid },
    role,
    roles,
    userRoleType,
  } = useSelector(authSelector || {});
  const theme = useTheme();
  const isDownSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const domainListOptions = useMemo(
    () =>
      domainsList?.map(({ uuid: id, name: label, id: userRoleDomainID }) => ({
        id,
        label,
        userRoleDomainID,
        selected: domainID === id,
      }))?.sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [domainID, domainsList],
  );
  const selectedValue = useMemo(
    () => domainListOptions.find(option => option.selected) || domainListOptions[0],
    [domainListOptions],
  );

  const setDomainID = useCallback(
    (_, value, action, option, isSharedDomain) => {
      if (!value) {
        return;
      }

      const { id: domainID, userRoleDomainID } = value;

      localStorage.setItem('domainID', domainID);
      localStorage.setItem('userRoleDomainID', userRoleDomainID);

      const currActiveDomainRole = userRoleType === UserLayer.Account ?
        roles.find(rl => rl.code === role.code) :
        domainsList.find(domain => domain.uuid === domainID).user_domain_roles.sort((a, b) => a.id - b.id)[0];

      dispatch(getFeatures());
      dispatch(resetFilters());

      if (!isSharedDomain) {
        dispatch(getPersonalView());
      }

      dispatch(resetActiveTableView());
      dispatch(setUserDomain({ domainID, userRoleDomainID, currActiveDomainRole }));
    },
    [roles, userRoleType, role],
  );

  useEffect(() => {
    if (sharedDomainUuid) {
      const domainMeta = domainListOptions.find(({ id }) => sharedDomainUuid === id);

      if (!domainMeta) {
        showToast({ status: 'error', message: 'You don’t have permission to view this domain.' });
        localStorage.setItem('rejectedDomainSwitch', true);
      } else {
        setDomainID(null, domainMeta, null, null, true);
      }

      localStorage.removeItem('sharedDomainUuid');
    } else if (defaultDomainUuid) {
      setDomainID(null, domainListOptions.find(({ id }) => defaultDomainUuid === id));
    }
  }, [defaultDomainUuid]);

  useEffect(() => {
    if (userID) {
      localStorage.setItem('domainName', window.location.origin);
    }
  }, [userID]);

  useEffect(() => {
    if (!domainID && Boolean(domainListOptions.length)) {
      if (dmID && domainListOptions.length) {
        const { id, userRoleDomainID } = domainListOptions.find(dm => dm.id === dmID) || domainListOptions[0];
        setDomainID(null, { id, userRoleDomainID });
        return;
      }

      const [{ id, userRoleDomainID }] = domainListOptions;
      setDomainID(null, { id, userRoleDomainID });
    }
  }, [dmID, domainID, domainListOptions, setDomainID]);

  const handleSetAsDefaultDomain = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const uuid = selectedValue.id === defaultDomainUuid ? null : selectedValue.id;
    dispatch(setDefaultDomain({ uuid }));
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <FormControl sx={{ width: isDownSmall ? 220 : 300, height: '100%', justifyContent: 'center' }}>
        <Autocomplete
          forcePopupIcon={false}
          onChange={setDomainID}
          options={domainListOptions}
          blurOnSelect
          disableClearable
          value={selectedValue}
          sx={{
            paddingRight: isDownSmall ? 0 : 2,
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none !important',
            },
            '& .MuiInputBase-input': {
              WebkitTextFillColor: '#453F91 !important',
              fontWeight: 600,
            },
          }}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Current Domain"
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: isDownSmall ? '15px' : '20px' } }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <IconButton
                    size="small"
                    onClick={handleSetAsDefaultDomain}
                  >
                    {domainListOptions.find(({ id }) => id === defaultDomainUuid)?.label === params.inputProps.value
                      ? <StarFilled  style={{ color: '#E7A955' }} />
                      : <StarOutlined />
                    }
                  </IconButton>
                ),
              }}
            />
          )}
        />
      </FormControl>
    </Box>
  );
};

export default DomainSelection;
